import apiClient from "./apiClient";

/**
 * Fetch Call Log Summary stats
 */
export const getCallLogSummary = async (companyCode, period = "today", customRange = null) => {
  try {
    const params = { companyCode };
    if (period === "all") {
      params.from = "2020-01-01";
      params.to = "2030-12-31";
    } else if (period === "last_week" || period === "lastweek") {
      params.period = "lastweek";
    } else if (period && period !== "custom") {
      params.period = period;
    } else if (customRange?.length === 2) {
      params.from = customRange[0];
      params.to = customRange[1];
    }

    const data = await apiClient.get("/api/call-logs/summary", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return data?.stats || null;
  } catch (error) {
    console.error("[Dashboard] Call summary error:", error);
    return null;
  }
};

/**
 * Fetch Call Log Timeline hourly/daily distribution for Chart
 */
export const getCallLogTimeline = async (companyCode, period = "today", customRange = null) => {
  try {
    const params = { companyCode };
    if (period === "all") {
      params.from = "2026-08-01";
      params.to = "2026-08-31";
    } else if (period === "last_week" || period === "lastweek") {
      params.period = "lastweek";
    } else if (period && period !== "custom") {
      params.period = period;
    } else if (customRange?.length === 2) {
      params.from = customRange[0];
      params.to = customRange[1];
    }

    const data = await apiClient.get("/api/call-logs/timeline", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return data?.timeline || [];
  } catch (error) {
    console.error("[Dashboard] Call timeline error:", error);
    return [];
  }
};

/**
 * Fetch Top Employees Call Statistics with Resolved Names
 */
export const getTopEmployees = async (companyCode, period = "today") => {
  try {
    const params = { companyCode };
    if (period === "all") {
      params.from = "2020-01-01";
      params.to = "2030-12-31";
    } else if (period === "last_week" || period === "lastweek") {
      params.period = "lastweek";
    } else if (period && period !== "custom") {
      params.period = period;
    }

    const headers = companyCode ? { "x-active-company-code": companyCode } : {};

    // Fetch employee call aggregates and employee directory in parallel
    const [callLogsRes, empListRes] = await Promise.all([
      apiClient.get("/api/call-logs/employees", { params, headers }).catch(() => null),
      apiClient.get("/api/employees", { params: { companyCode }, headers }).catch(() => null),
    ]);

    const rawEmpCallData = callLogsRes?.employees || (Array.isArray(callLogsRes) ? callLogsRes : []);
    const empDirectory = empListRes?.employees || empListRes?.data || (Array.isArray(empListRes) ? empListRes : []);

    // Create lookup map of call aggregates by ID, mobile, and lowercase name
    const callMap = new Map();
    rawEmpCallData.forEach((item) => {
      if (item.employeeId) callMap.set(String(item.employeeId), item);
      if (item.mobile) callMap.set(String(item.mobile), item);
      if (item.name) callMap.set(String(item.name).toLowerCase(), item);
    });

    const seen = new Set();
    const mergedList = [];

    // 1. Process all registered employees from company directory
    empDirectory.forEach((emp) => {
      const empIdKey = String(emp._id || emp.id || emp.mobile || "");
      if (empIdKey && seen.has(empIdKey)) return;
      if (empIdKey) seen.add(empIdKey);

      const callStats =
        callMap.get(String(emp._id)) ||
        callMap.get(String(emp.id)) ||
        callMap.get(String(emp.mobile)) ||
        callMap.get(String(emp.name || "").toLowerCase()) ||
        {};

      mergedList.push({
        employeeId: emp._id || emp.id || emp.employeeId || "",
        name: emp.name || emp.employeeName || "Representative",
        mobile: emp.mobile || emp.phone || "—",
        profilePhoto: emp.profilePhoto || "",
        incoming: callStats.incoming || 0,
        outgoing: callStats.outgoing || 0,
        missed: callStats.missed || 0,
        rejected: callStats.rejected || 0,
        connected: callStats.connected || 0,
        totalDuration: callStats.totalDuration || 0,
      });
    });

    // 2. Include any call log records not yet in company directory
    rawEmpCallData.forEach((item) => {
      const itemKey = String(item.employeeId || item.mobile || "");
      if (itemKey && !seen.has(itemKey)) {
        seen.add(itemKey);
        mergedList.push({
          employeeId: item.employeeId || "",
          name: item.name || "Representative",
          mobile: item.mobile || "—",
          profilePhoto: item.profilePhoto || "",
          incoming: item.incoming || 0,
          outgoing: item.outgoing || 0,
          missed: item.missed || 0,
          rejected: item.rejected || 0,
          connected: item.connected || 0,
          totalDuration: item.totalDuration || 0,
        });
      }
    });

    // Sort by total calls DESC, total duration DESC, then name ASC
    mergedList.sort((a, b) => {
      const totalA = (a.incoming || 0) + (a.outgoing || 0) + (a.missed || 0) + (a.rejected || 0);
      const totalB = (b.incoming || 0) + (b.outgoing || 0) + (b.missed || 0) + (b.rejected || 0);
      if (totalB !== totalA) return totalB - totalA;
      if ((b.totalDuration || 0) !== (a.totalDuration || 0)) return (b.totalDuration || 0) - (a.totalDuration || 0);
      return (a.name || "").localeCompare(b.name || "");
    });

    return mergedList;
  } catch (error) {
    console.error("[Dashboard] Employees error:", error);
    return [];
  }
};

/**
 * Fetch Invoices & Revenue Aggregate
 */
export const getInvoiceRevenue = async (companyCode) => {
  try {
    const headers = companyCode ? { "x-active-company-code": companyCode } : {};

    // 1. Try lightweight aggregate summary endpoint
    try {
      const summaryData = await apiClient.get("/api/invoices/summary", {
        params: { companyCode },
        headers,
        timeout: 5000,
      });

      if (summaryData?.stats) {
        return {
          totalAmount: Number(summaryData.stats.totalAmount || 0),
          amountPaid: Number(summaryData.stats.totalPaid || 0),
          balanceDue: Number(summaryData.stats.totalPending || 0),
          invoiceCount: Number(summaryData.stats.invoiceCount || summaryData.total || 0),
        };
      }
    } catch {
      // Fall through to paginated fallback
    }

    // 2. Fallback to /api/invoices with pageSize=1
    const data = await apiClient.get("/api/invoices", {
      params: { companyCode, pageSize: 1 },
      headers,
      timeout: 8000,
    });

    const invoices = data?.invoices || data?.data || (Array.isArray(data) ? data : []);

    if (data?.stats && (data.stats.totalAmount || data.stats.totalPaid || data.stats.totalPending)) {
      return {
        totalAmount: Number(data.stats.totalAmount || 0),
        amountPaid: Number(data.stats.totalPaid || 0),
        balanceDue: Number(data.stats.totalPending || 0),
        invoiceCount: Number(data.total || invoices.length || 0),
      };
    }

    let totalAmount = 0;
    let amountPaid = 0;
    let balanceDue = 0;

    invoices.forEach((inv) => {
      totalAmount += Number(inv.total || 0);
      amountPaid += Number(inv.amountPaid || (inv.paymentStatus === "paid" ? inv.total : 0) || 0);
      balanceDue += Number(inv.balanceDue || (inv.paymentStatus !== "paid" ? inv.total : 0) || 0);
    });

    return {
      totalAmount,
      amountPaid,
      balanceDue: balanceDue || Math.max(0, totalAmount - amountPaid),
      invoiceCount: invoices.length,
    };
  } catch (error) {
    console.warn("[Dashboard] Invoice revenue fetch failed (will keep last data):", error?.message || error);
    return null;
  }
};
